import CustomAvatar from '-/Custom/Avatar';
import { Link, Stack, useLocalSearchParams } from 'expo-router';
import { Pressable, StyleSheet, View } from 'react-native';
import { useQuery } from 'urql';
import Box from '@/components/UI/Box';
import Text from '@/components/UI/Text';
import Icon from '@/components/Unistyles/Icon';
import { graphql } from '@/gql/graphql';
import useCurrentUser from '@/hooks/useCurrentUser';
import pluralize from '@/utils/pluralize';

const style = StyleSheet.create({
  container: {
    alignItems: 'flex-start',
    justifyContent: 'center',
    flex: 1,
  },
  avatarContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginRight: 8,
  },
  titleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    maxWidth: 200,
  },
});

const GetChatHeader = graphql(/* GraphQL */ `
  query GetChatHeader($chatId: uuid!) {
    chat_by_pk(id: $chatId) {
      id
      preference {
        chat_id
        person_id
        notification_preference
      }
      metadata
      chat_type
    }
  }
`);

function ChatHeader() {
  const { chatId } = useLocalSearchParams<{ chatId?: string }>();

  const [{ data }] = useQuery({
    query: GetChatHeader,
    variables: { chatId: chatId || '' },
  });

  const chat = data?.chat_by_pk;
  const user = useCurrentUser();
  const isSilenced =
    (chat?.preference?.notification_preference ??
      user?.chat_notification_preference ??
      'all') === 'none';

  const title = () => (
    <Link
      href={{
        pathname: '/chat/[chatId]/info',
        params: { chatId },
      }}
      asChild
    >
      <Pressable style={style.container} testID="ChatDetailHeaderButton">
        <View style={style.titleContainer}>
          <View style={style.avatarContainer}>
            <CustomAvatar
              id={chat?.metadata?.avatar_id ?? undefined}
              name={chat?.metadata?.title || ''}
              color={chat?.metadata?.color}
              size="md"
            />
          </View>
          <Box direction="horizontal" alignItems="center" space="sm">
            <Box>
              <Text
                bold
                size="xl"
                numberOfLines={1}
                testID="ChatDetailHeaderTitle"
              >
                {chat?.metadata?.title || ''}
              </Text>
              <Box direction="horizontal" space="xs" alignItems="center">
                {isSilenced && (
                  <Icon name="notifications-off-outline" size="xs" />
                )}
                {chat?.chat_type === 'team' && (
                  <Text
                    variant="typographySecondary"
                    size="sm"
                    numberOfLines={1}
                  >
                    Team Chat ({chat?.metadata?.participant_count}{' '}
                    {pluralize('Member', chat?.metadata?.participant_count)})
                  </Text>
                )}
                {chat?.chat_type === 'dm' && (
                  <Text
                    variant="typographySecondary"
                    size="sm"
                    numberOfLines={1}
                  >
                    Direct Message
                  </Text>
                )}
                {chat?.chat_type === 'group' && (
                  <Text
                    variant="typographySecondary"
                    size="sm"
                    numberOfLines={1}
                  >
                    {chat?.metadata?.participant_count} People
                  </Text>
                )}
              </Box>
            </Box>
          </Box>
        </View>
      </Pressable>
    </Link>
  );

  return (
    <Stack.Screen
      options={{
        headerTitle: title,
        headerBackButtonDisplayMode: 'minimal',
        title: chat?.metadata?.title || '',
      }}
    />
  );
}

export default ChatHeader;
